import React, { useEffect } from 'react';
import { validateAuth } from "../auth/service";
import { getUserRole } from "../auth/service/storage.service";
import { Navigate, useNavigate } from "react-router-dom";
import Loading from "./loading";
import { useAuth } from '../auth/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  fallbackPath?: string;
}

const ProtectedRoute = ({ 
  children, 
  allowedRoles, 
  fallbackPath = '/deliveries' 
}: ProtectedRouteProps) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
  const [hasAccess, setHasAccess] = React.useState<boolean | null>(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    const checkAuth = async () => {
      const isValid = await validateAuth();
      setIsAuthenticated(isValid);
      
      if (isValid) {
        // Si no hay roles específicos, cualquier usuario autenticado tiene acceso
        if (!allowedRoles || allowedRoles.length === 0) {
          setHasAccess(true);
          return;
        }
        
        // Verificar si el usuario tiene un rol permitido
        const userRole = getUserRole();
        const hasRole = userRole ? allowedRoles.includes(userRole) : false;
        setHasAccess(hasRole);
        
        // Si no tiene el rol adecuado, redirigir a la ruta alternativa
        if (!hasRole) {
          navigate(fallbackPath, { replace: true });
        }
      } else {
        // Si no está autenticado, cerrar sesión
        logout();
      }
    };
    
    checkAuth();
  }, [navigate, allowedRoles, fallbackPath, logout]);
  
  if (isAuthenticated === null || (isAuthenticated && hasAccess === null)) {
    // Mientras verifica, muestra un spinner o pantalla de carga
    return <Loading text="Verificando permisos..." />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }
  
  return hasAccess ? <>{children}</> : <Navigate to={fallbackPath} replace />;
};

export default ProtectedRoute;